import React, { useState, useRef, useEffect } from 'react';

const Certificates = ({ students }) => {
    const [selectedStudentLrn, setSelectedStudentLrn] = useState('');
    const [certType, setCertType] = useState('Good Moral');
    const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
    
    // New states for the Smart Search feature
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const searchRef = useRef(null);

    // Find the specific student data based on the locked-in LRN
    const student = students?.find(s => s.lrn === selectedStudentLrn);

    // Filter students as the staff types (searches by Name or LRN)
    const filteredStudents = students?.filter(s => {
        const fullName = `${s.first_name} ${s.last_name}`.toLowerCase();
        const lrn = (s.lrn || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        return fullName.includes(search) || lrn.includes(search);
    }) || [];

    // Format the date to look formal (e.g., "18th day of May, 2026")
    const getFormalDate = (dateString) => {
        const d = new Date(dateString);
        return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    // Handle selection from the search dropdown
    const handleSelectStudent = (selectedStudent) => {
        setSelectedStudentLrn(selectedStudent.lrn);
        setSearchTerm(`${selectedStudent.last_name}, ${selectedStudent.first_name}`);
        setIsSearching(false); // Hide the dropdown
    };

    // Close the dropdown if the user clicks anywhere else on the screen
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsSearching(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
            
            {/* --- CONTROL PANEL (Hidden on Print) --- */}
            <div className="no-print" style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '2px solid #e2e8f0' }}>
                <h2 style={{ color: '#8b5cf6', margin: '0 0 10px 0' }}>🎓 Certificate Generator</h2>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                    
                    {/* SMART STUDENT SEARCH */}
                    <div style={{ flex: 1, minWidth: '250px', position: 'relative' }} ref={searchRef}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Search Student (Name or LRN)</label>
                        <input 
                            type="text" 
                            placeholder="Type to search..." 
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setIsSearching(true);
                                setSelectedStudentLrn(''); // Clear active selection if they start typing a new name
                            }}
                            onFocus={() => setIsSearching(true)}
                            style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}
                        />
                        
                        {/* Floating Search Results Dropdown */}
                        {isSearching && searchTerm.length > 0 && (
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', marginTop: '5px', maxHeight: '200px', overflowY: 'auto', zIndex: 50, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }}>
                                {filteredStudents.length > 0 ? (
                                    filteredStudents.map(s => (
                                        <div 
                                            key={s.lrn} 
                                            onClick={() => handleSelectStudent(s)}
                                            style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                                        >
                                            <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{s.last_name}, {s.first_name}</div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>LRN: {s.lrn} | {s.grade_level}</div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: '15px', textAlign: 'center', color: '#94a3b8' }}>
                                        No students found matching "{searchTerm}"
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Certificate Type */}
                    <div style={{ flex: 1, minWidth: '200px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Document Type</label>
                        <select value={certType} onChange={(e) => setCertType(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }}>
                            <option value="Good Moral">Certificate of Good Moral Character</option>
                            <option value="Enrollment">Certificate of Enrollment</option>
                            <option value="Graduation">Certificate of Graduation</option>
                        </select>
                    </div>

                    {/* Print Button */}
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        <button 
                            onClick={() => window.print()} 
                            disabled={!student}
                            style={{ padding: '10px 20px', backgroundColor: student ? '#3b82f6' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '6px', cursor: student ? 'pointer' : 'not-allowed', fontWeight: 'bold', height: '42px' }}
                        >
                            🖨️ Print Document
                        </button>
                    </div>
                </div>
            </div>

            {/* --- LIVE PREVIEW & PRINT TEMPLATE --- */}
            {student ? (
                <div className="certificate-paper" style={{ border: '8px double #1e293b', padding: '40px', textAlign: 'center', fontFamily: '"Times New Roman", Times, serif', maxWidth: '800px', margin: '0 auto', backgroundColor: 'white' }}>
                 {/* --- THE WATERMARK --- */}
                <div style={{
                  position: 'absolute',
                     top: '50%',
                     left: '50%',
                     transform: 'translate(-50%, -50%)',
                     opacity: '0.08', /* 8% opacity makes it a perfect, subtle watermark */
                     pointerEvents: 'none', /* Ensures it doesn't block you from clicking things */
                     zIndex: 0 /* Pushes it behind the text */
                    }}>
                   <img src="/owl.png" alt="SHAIS Watermark" style={{ width: '450px' }} />
                </div>

                    {/* --- OFFICIAL LETTERHEAD --- */}
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '40px' }}>
                        <img src="/owl.png" alt="SHAIS Logo" style={{ width: '100px', height: 'auto' }} />
                        <div style={{ textAlign: 'center', color: 'black' }}>
                            <h2 style={{ margin: '0 0 5px 0', fontSize: '26px', textTransform: 'uppercase', fontWeight: 'bold', fontFamily: 'Georgia, "Times New Roman", serif', letterSpacing: '1px' }}>
                                San Herminigildo Agro-Industrial School
                            </h2>
                            <p style={{ margin: '0 0 5px 0', fontSize: '15px' }}>
                                Recognition Permit No. 139, s. 1970 &nbsp;|&nbsp; School ID: 404993
                            </p>
                            <p style={{ margin: '0 0 5px 0', fontSize: '15px' }}>
                                P4 Alanib, Lantapan, 8722 Bukidnon
                            </p>
                            <p style={{ margin: 0, fontSize: '14px' }}>
                                Email: <span style={{ color: 'blue', textDecoration: 'underline' }}>shaisfi@yahoo.com</span> &nbsp;|&nbsp; Contact no. 09175756967 &nbsp;|&nbsp; 09178765447
                            </p>
                        </div>
                    </div>

                    <h1 style={{ fontSize: '36px', letterSpacing: '2px', textTransform: 'uppercase', color: '#1e293b', borderBottom: '2px solid #1e293b', display: 'inline-block', paddingBottom: '10px', marginBottom: '50px' }}>
                        Certificate of {certType}
                    </h1>

                    {/* Dynamic Body Content based on selection */}
                    <p style={{ fontSize: '18px', textAlign: 'justify', lineHeight: '2', marginBottom: '50px', position: 'relative', zIndex: 10 }}>
                        TO WHOM IT MAY CONCERN:
                        <br /><br />
                        This is to certify that <strong>{student.first_name} {student.middle_name ? student.middle_name + ' ' : ''}{student.last_name}</strong> (LRN: <strong>{student.lrn}</strong>), 
                        is a bonafide student of this institution under the section of {student.grade_level} - {student.section || 'General'}.
                        
                        {certType === 'Good Moral' && (
                            <span> During their stay in this institution, they have displayed good moral character and have not been subjected to any disciplinary action.</span>
                        )}
                        {certType === 'Enrollment' && (
                            <span> This certification is issued to confirm their official enrollment for the current academic year.</span>
                        )}
                        {certType === 'Graduation' && (
                            <span> This certification is issued to verify that they have satisfactorily completed the academic requirements for graduation.</span>
                        )}
                        
                        <br /><br />
                        Issued this <strong>{getFormalDate(issueDate)}</strong> at San Herminigildo Agro-Industrial School, Alanib, Lantapan, Bukidnon for whatever legal purpose it may serve.
                    </p>

                    {/* --- FOOTER: SEAL AND SIGNATURE --- */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '60px' }}>
                        
                        {/* Left Side: 1.5 Inch Seal Space */}
                        <div style={{ textAlign: 'center', width: '2in' }}>
                            <div style={{ width: '1.5in', height: '1.5in', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span className="no-print" style={{ color: '#cbd5e1', fontSize: '10px' }}>(Seal Area)</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '14px', fontStyle: 'italic', fontWeight: 'bold', color: '#1e293b' }}>Not valid without seal</p>
                        </div>

                        {/* Right Side: Conditional Signatory */}
                        <div style={{ textAlign: 'center', minWidth: '2.5in' }}>
                            {certType === 'Good Moral' ? (
                                <>
                                    <p style={{ margin: 0, fontWeight: 'bold', fontSize: '18px', textDecoration: 'underline', textTransform: 'uppercase' }}>KIM ANDRIAN L. TAN</p>
                                    <p style={{ margin: 0, fontSize: '14px' }}>School Prefect</p>
                                </>
                            ) : (
                                <>
                                    <p style={{ margin: 0, fontWeight: 'bold', fontSize: '18px', textDecoration: 'underline', textTransform: 'uppercase' }}>ALYSSA L. TAN</p>
                                    <p style={{ margin: 0, fontSize: '14px' }}>School Principal</p>
                                </>
                            )}
                        </div>
                    </div>

                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '50px', color: '#94a3b8' }}>
                    Search and select a student above to preview the document.
                </div>
            )}
        </div>
    );
};

export default Certificates;